/**
 * Purpose: Displays a trivia question styled as torn notebook paper taped to the chalkboard.
 * Owns: Question text rendering, category label display, notebook paper texture, tape corners.
 * Notes: The card takes ~35-40% of the screen height per CLAUDE.md layout spec.
 *        Category label uses colored "chalk" styling. Paper is held by tape at top corners
 *        (NOT pins — pins don't make sense on a chalkboard). Uses CSS texture classes
 *        from chalkboard-textures.css for the paper and tape effects.
 */

import type { Question, Category } from '../../types/question';

interface QuestionCardProps {
  /** The question to display */
  question: Question;
}

/**
 * Category-to-CSS-variable mapping for colored chalk labels.
 * Each subject gets a distinct accent color from the theme.
 */
const CATEGORY_COLORS: Record<Category, string> = {
  Math: 'var(--btn-d)',
  Science: 'var(--btn-c)',
  History: 'var(--btn-a)',
  English: 'var(--accent-gold)',
  Art: 'var(--btn-b)',
  Geography: 'var(--btn-c)',
  Music: 'var(--btn-d)',
  Sports: 'var(--btn-a)',
};

/** Emoji icons for each subject (displayed beside the category label). */
const CATEGORY_ICONS: Record<Category, string> = {
  Math: '\u{1F9EE}',
  Science: '\u{1F52C}',
  History: '\u{1F4DC}',
  English: '\u{1F4D6}',
  Art: '\u{1F3A8}',
  Geography: '\u{1F30D}',
  Music: '\u{1F3B5}',
  Sports: '\u{1F3C8}',
};

export function QuestionCard({ question }: QuestionCardProps) {
  const categoryColor = CATEGORY_COLORS[question.category];
  const categoryIcon = CATEGORY_ICONS[question.category];

  return (
    <div
      className="notebook-paper"
      style={styles.card}
      role="region"
      aria-label="Question card"
    >
      {/* Tape pieces at top corners holding paper to chalkboard */}
      <div className="tape-left" aria-hidden="true" />
      <div className="tape-right" aria-hidden="true" />

      {/* Category label in colored chalk style */}
      <div style={{ ...styles.categoryLabel, color: categoryColor }}>
        <span>{categoryIcon}</span>
        <span style={styles.categoryText}>{question.category}</span>
      </div>

      {/* Question text — Patrick Hand handwriting font */}
      <p style={styles.questionText}>{question.question}</p>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    borderRadius: '3px',
    padding: '2rem 1.5rem 1.5rem 4rem',
    position: 'relative',
    width: '100%',
    maxWidth: '440px',
    margin: '0.75rem auto 0',
  },
  categoryLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '0.85rem',
    fontWeight: '700',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.75rem',
    fontFamily: 'var(--font-family)',
  },
  categoryText: {
    fontFamily: 'var(--font-family)',
  },
  questionText: {
    fontSize: '1.25rem',
    lineHeight: '1.75',
    color: 'var(--text-dark)',
    fontWeight: '400',
    fontFamily: 'var(--font-family-handwriting)',
  },
};
